import { useState } from "react";
import { toast } from "sonner";
import { Phone, MessageCircle } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { SITE } from "@/constants/site";
import { FadeUp, RevealLineInView } from "@/components/animations/Reveal";

export default function Booking() {
    const { t } = useLanguage();
    const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onSubmit = (e) => {
        e.preventDefault();
        const lines = [
            `${t.booking.form.name}: ${form.name}`,
            `${t.booking.form.phone}: ${form.phone}`,
            `${t.booking.form.email}: ${form.email}`,
            "",
            form.message,
        ];
        const url = `${SITE.whatsapp}?text=${encodeURIComponent(lines.join("\n"))}`;
        window.open(url, "_blank", "noopener,noreferrer");
        toast.success(t.booking.form.success);
        setForm({ name: "", phone: "", email: "", message: "" });
    };

    return (
        <section id="booking" className="relative bg-white py-16 md:py-36" data-testid="booking">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                <div className="flex items-center gap-4 mb-8">
                    <span className="chapter-num text-lg">{t.booking.chapter}</span>
                    <span className="h-[1px] flex-1 bg-gold/40" />
                    <span className="eyebrow">{t.booking.eyebrow}</span>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                    {/* Left: WhatsApp CTA */}
                    <div className="lg:col-span-5">
                        <h2 className="font-display text-ink leading-[0.98] text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-8">
                            <RevealLineInView>{t.booking.title.split(" ")[0]}</RevealLineInView>{" "}
                            <span className="serif-italic text-gold">
                                <RevealLineInView delay={0.1}>{t.booking.title.split(" ").slice(1).join(" ")}</RevealLineInView>
                            </span>
                        </h2>

                        <FadeUp delay={0.1}>
                            <p className="text-ink/75 text-lg leading-relaxed mb-10 max-w-md">
                                {t.booking.sub}
                            </p>
                        </FadeUp>

                        <FadeUp delay={0.2}>
                            <div className="flex flex-col gap-4 max-w-sm">
                                <a
                                    href={SITE.whatsapp}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-gold inline-flex items-center justify-center gap-3"
                                    data-testid="booking-whatsapp"
                                >
                                    <MessageCircle className="w-4 h-4" />
                                    {t.booking.whatsapp}
                                </a>
                                <a
                                    href={`tel:${SITE.phoneRaw}`}
                                    className="btn-outline inline-flex items-center justify-center gap-3"
                                    data-testid="booking-call"
                                >
                                    <Phone className="w-4 h-4" />
                                    {t.booking.call} · {SITE.phone}
                                </a>
                            </div>
                        </FadeUp>

                        <FadeUp delay={0.3} className="mt-12 pt-10 border-t border-gold/25">
                            <div className="serif-italic text-gold text-sm mb-2">Frizerka</div>
                            <div className="text-ink font-sans">{SITE.address.street}</div>
                            <div className="text-ink/70 font-sans">{SITE.address.city}</div>
                            <div className="text-ink/70 font-sans">{SITE.address.country}</div>
                        </FadeUp>
                    </div>

                    {/* Right: Form */}
                    <FadeUp delay={0.15} className="lg:col-span-7">
                        <form
                            onSubmit={onSubmit}
                            className="bg-cream rounded-3xl p-6 md:p-12 border border-gold/25"
                            data-testid="booking-form"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <Field
                                    label={t.booking.form.name}
                                    name="name"
                                    value={form.name}
                                    onChange={onChange}
                                    required
                                />
                                <Field
                                    label={t.booking.form.phone}
                                    name="phone"
                                    value={form.phone}
                                    onChange={onChange}
                                    required
                                />
                                <div className="md:col-span-2">
                                    <Field
                                        label={t.booking.form.email}
                                        name="email"
                                        type="email"
                                        value={form.email}
                                        onChange={onChange}
                                        required
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block eyebrow mb-3">{t.booking.form.message}</label>
                                    <textarea
                                        name="message"
                                        rows={5}
                                        value={form.message}
                                        onChange={onChange}
                                        required
                                        placeholder={t.booking.form.placeholder}
                                        className="w-full bg-white border border-gold/25 rounded-2xl px-5 py-4 text-ink placeholder-ink/40 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/50 transition-colors font-sans resize-none"
                                        data-testid="booking-input-message"
                                    />
                                </div>
                            </div>

                            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                <p className="text-xs text-ink/60 max-w-sm">
                                    {SITE.email} · {SITE.phone}
                                </p>
                                <button
                                    type="submit"
                                    className="btn-gold inline-flex items-center gap-2"
                                    data-testid="booking-submit"
                                >
                                    <MessageCircle className="w-4 h-4" />
                                    {t.booking.form.submit}
                                </button>
                            </div>
                        </form>
                    </FadeUp>
                </div>
            </div>
        </section>
    );
}

function Field({ label, name, value, onChange, type = "text", required }) {
    return (
        <div>
            <label className="block eyebrow mb-3">{label}</label>
            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                required={required}
                data-testid={`booking-input-${name}`}
                className="w-full bg-white border border-gold/25 rounded-full px-5 py-3.5 text-ink placeholder-ink/40 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/50 transition-colors font-sans"
            />
        </div>
    );
}
